/**
 * 快速开始页面 - 上传招标文件自动创建项目
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  SparklesIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { documentApi, projectApi } from '../services/api';
import LoadingOverlay from '../components/LoadingOverlay';
import Toast, { ToastType } from '../components/Toast';

interface QuickStartProps {
  onProjectCreated: (projectId: string, fileContent: string, projectName?: string) => void;
}

const FILE_FORMATS = [
  { label: 'Word', ext: '.docx / .doc', bg: '#dbeafe', color: '#1d4ed8', char: 'W' },
  { label: 'PDF', ext: '.pdf', bg: '#fee2e2', color: '#dc2626', char: 'P' },
  { label: 'TXT', ext: '.txt', bg: '#cffafe', color: '#0e7490', char: 'T' },
  { label: 'MD', ext: '.md', bg: '#f3e8ff', color: '#7c3aed', char: 'M' },
];

const HIGHLIGHTS = [
  'AI 智能解析招标文件，自动提取核心要求',
  '五步流程生成完整专业标书',
  '支持 Word、PDF、TXT、Markdown 格式',
  '单文件最大 50 MB，安全高效处理',
];

const QuickStart: React.FC<QuickStartProps> = ({ onProjectCreated }) => {
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({ show: false, type: 'info', title: '', messages: [] });

  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  const closeToast = () => setToast((t) => ({ ...t, show: false }));

  const processFile = async (file: File) => {
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
      'text/markdown',
    ];
    const allowedExtensions = ['.pdf', '.docx', '.doc', '.txt', '.md'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      showToast('error', '不支持的文件类型', ['请上传 PDF、Word、TXT 或 Markdown 格式的招标文件']);
      return;
    }

    try {
      setUploading(true);
      setLoadingMessage('正在创建项目...');

      const tempProjectName = file.name.replace(/\.[^/.]+$/, '');
      const projectData = {
        name: tempProjectName,
        company_credit_code: '',  // 快速创建时不指定主体，后续可在配置中设置
        project_type: '数据类',
        word_count: 50000,
        project_price: 0,
        file_content: '',
        ai_model_name: '千问',
        selected_model: '',
      };

      const createResponse = await projectApi.createProject(projectData);
      const projectId = createResponse.data.id;

      await projectApi.updateProgress(projectId, { current_step: 0, max_reached_step: 0 });

      setLoadingMessage('正在上传招标文件...');
      const uploadResponse = await documentApi.uploadFile(file, 'tender', projectId);

      if (!uploadResponse.data.success) {
        showToast('error', '文件上传失败', [uploadResponse.data.message]);
        return;
      }

      const fileContent = uploadResponse.data.file_content || '';
      onProjectCreated(projectId, fileContent, tempProjectName);
    } catch (error: any) {
      showToast('error', '创建项目失败', [error.response?.data?.detail || '请检查网络连接后重试']);
    } finally {
      setUploading(false);
      setParsing(false);
      setLoadingMessage('');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
    event.target.value = '';
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (uploading || parsing) return;
      const files = e.dataTransfer.files;
      if (files.length === 0) return;
      await processFile(files[0]);
    },
    [uploading, parsing]
  );

  const isLoading = uploading || parsing;

  return (
    <div
      className="w-full h-full flex items-center justify-center overflow-hidden relative"
      style={{ background: 'linear-gradient(135deg, #f0f6ff 0%, #f8fafc 50%, #f5f3ff 100%)' }}
    >
      <LoadingOverlay show={isLoading} message={loadingMessage} />
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
      />

      {/* 背景装饰 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full"
          style={{
            top: '-5%', right: '5%',
            width: '400px', height: '400px',
            background: 'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            bottom: '5%', left: '25%',
            width: '350px', height: '350px',
            background: 'radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 65%)',
          }}
        />
      </div>

      {/*
        响应式主容器：
        - 移动端/窄屏：单列，上下排列
        - 中等屏（md）：左侧偏移避开 StepSidebar，两列水平排列
        - ml-[260px] 在 md+ 时避开展开的侧边栏（240px + gap）
      */}
      <div className="relative z-10 w-full h-full flex items-center justify-center px-4 md:pl-[276px] md:pr-8 lg:pr-12 xl:pr-20 py-6">
        <div className="w-full max-w-5xl flex flex-col md:flex-row items-center gap-8 lg:gap-14 xl:gap-20">

          {/* ─── 左侧：介绍区 ─── */}
          <div className="flex-shrink-0 w-full md:w-[340px] lg:w-[400px] xl:w-[440px] space-y-6 text-center md:text-left">
            {/* 徽章 */}
            <div className="flex justify-center md:justify-start">
              <div
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium"
                style={{
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  color: '#1d4ed8',
                }}
              >
                <SparklesIcon className="w-3.5 h-3.5" />
                AI 智能标书助手
              </div>
            </div>

            {/* 标题 */}
            <div>
              <h1
                className="text-2xl lg:text-3xl xl:text-4xl font-black leading-tight mb-3"
                style={{ color: '#0f172a' }}
              >
                上传招标文件
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(90deg, #1d4ed8 0%, #0ea5e9 100%)' }}
                >
                  一键开始创作
                </span>
              </h1>
              <div
                className="h-1 w-16 rounded-full mx-auto md:mx-0"
                style={{ background: 'linear-gradient(90deg, #1d4ed8, #0ea5e9)' }}
              />
            </div>

            {/* 特性列表 */}
            <div className="space-y-2.5">
              {HIGHLIGHTS.map((h) => (
                <div key={h} className="flex items-start gap-2.5 justify-center md:justify-start">
                  <CheckCircleIcon
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    style={{ color: '#0ea5e9' }}
                  />
                  <span className="text-sm leading-relaxed" style={{ color: '#475569' }}>
                    {h}
                  </span>
                </div>
              ))}
            </div>

            {/* 支持格式 */}
            <div>
              <p className="text-xs font-medium mb-3 uppercase tracking-wider" style={{ color: '#94a3b8' }}>
                支持格式
              </p>
              <div className="grid grid-cols-4 gap-2">
                {FILE_FORMATS.map(({ label, ext, bg, color, char }) => (
                  <div
                    key={label}
                    className="flex flex-col items-center py-3 px-2 rounded-xl"
                    style={{ background: bg, border: `1px solid ${color}20` }}
                  >
                    <span className="text-lg font-black mb-0.5" style={{ color }}>
                      {char}
                    </span>
                    <span className="text-xs font-semibold" style={{ color }}>
                      {label}
                    </span>
                    <span className="text-xs mt-0.5 text-center leading-tight" style={{ color: `${color}99` }}>
                      {ext}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ─── 右侧：上传区 ─── */}
          <div className="flex-1 w-full min-w-0">
            <div
              className="relative rounded-2xl lg:rounded-3xl transition-all duration-300 cursor-pointer overflow-hidden"
              style={{
                background: isDragging
                  ? 'linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)'
                  : 'rgba(255,255,255,0.9)',
                border: `2px dashed ${isDragging ? '#3b82f6' : '#cbd5e1'}`,
                boxShadow: isDragging
                  ? '0 0 0 4px rgba(59,130,246,0.12), 0 8px 40px rgba(59,130,246,0.15)'
                  : '0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)',
                backdropFilter: 'blur(12px)',
              }}
              onClick={() => !isLoading && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onMouseEnter={(e) => {
                if (!isLoading && !isDragging) {
                  e.currentTarget.style.borderColor = '#93c5fd';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(59,130,246,0.12), 0 2px 8px rgba(0,0,0,0.04)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDragging) {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)';
                }
              }}
            >
              <div className="flex flex-col items-center py-12 px-8 lg:py-16 lg:px-16 xl:py-20 xl:px-24 space-y-6">
                {/* 上传图标 */}
                <div
                  className="relative flex items-center justify-center"
                  style={{ width: '88px', height: '88px' }}
                >
                  <div
                    className="absolute inset-0 rounded-2xl"
                    style={{
                      background: isDragging
                        ? 'linear-gradient(135deg, #dbeafe, #bfdbfe)'
                        : 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                    }}
                  />
                  <div
                    className="absolute inset-0 rounded-2xl opacity-30"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59,130,246,0.2), transparent)',
                    }}
                  />
                  <ArrowUpTrayIcon
                    className="relative w-10 h-10 transition-transform duration-200"
                    style={{
                      color: isDragging ? '#1d4ed8' : '#3b82f6',
                      transform: isDragging ? 'translateY(-3px)' : 'translateY(0)',
                    }}
                  />
                </div>

                {/* 主文字 */}
                <div className="text-center space-y-2">
                  <p
                    className="text-lg lg:text-xl font-semibold"
                    style={{ color: isDragging ? '#1d4ed8' : '#1e293b' }}
                  >
                    {isDragging ? '松开即可上传文件' : '拖拽文件到此处上传'}
                  </p>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>
                    支持 PDF、Word、TXT、Markdown · 最大 50 MB
                  </p>
                </div>

                {/* 分隔线 */}
                <div className="flex items-center gap-3 w-full max-w-xs">
                  <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
                  <span className="text-xs font-medium px-2" style={{ color: '#cbd5e1' }}>
                    或者
                  </span>
                  <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
                </div>

                {/* 按钮 */}
                <button
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #1d4ed8, #0ea5e9)',
                    color: '#fff',
                    boxShadow: '0 4px 16px rgba(29, 78, 216, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) e.currentTarget.style.filter = 'brightness(1.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'none';
                  }}
                >
                  <DocumentTextIcon className="w-4 h-4" />
                  选择本地文件
                </button>

                {/* 底部小字提示 */}
                <p className="text-xs text-center" style={{ color: '#cbd5e1' }}>
                  上传后将自动创建项目并跳转至标书解析步骤
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.txt,.md"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStart;
